export type IssuerKycPath = "simplified" | "standard";

export type IssuerKycState = {
	kind: IssuerKycPath;
	status: TKycStatus;
	stage: TKycStage;
	review?: IKycReview;
};

const STATUS_PRIORITY: TKycStatus[] = ["error", "declined", "expired", "under_review", "pending", "none"];

/** `need_kyc` identifies the B2C BIN path; every other BIN uses simplified B2B. */
export const getBinKycPath = (info: Pick<IBinInfo, "need_kyc"> | null): IssuerKycPath =>
	info?.need_kyc ? "standard" : "simplified";

/** Selects one alternative KYC path for the BIN without detaching status from stage. */
export const getIssuerKycState = (
	issuers: IKycIssuer[],
	issuer: number | null | undefined,
	kind: IssuerKycPath,
): IssuerKycState | null => {
	if (!Number.isInteger(issuer)) return null;
	const selected = issuers.find(item => item.issuer === issuer);
	if (!selected) return null;
	const entries = Array.isArray(selected.kyc) ? selected.kyc : [];
	const entry = entries.find(item => item.kind === kind)
		?? (entries.length === 1 ? entries[0] : undefined);
	if (!entry) return null;

	// Older responses had no stage. Approved meant a completed provider gate;
	// review metadata was only present while the issuer checked a holder.
	const stage = entry.stage ?? (entry.status === "approved" || selected.review ? "issuer" : "our_kyc");
	return { kind, status: entry.status, stage, review: selected.review };
};

/**
 * Compatibility for recovery screens that do not know the BIN kind. It is
 * intentionally conservative: a ready path can never unlock card creation.
 */
export const getIssuerKycStatus = (
	issuers: IKycIssuer[],
	issuer: number | null | undefined,
): TKycStatus => {
	if (!Number.isInteger(issuer)) return "none";
	const entries = issuers.find(item => item.issuer === issuer)?.kyc ?? [];
	if (entries.length === 0 || entries.some(entry => entry.stage === "ready")) return "none";
	const statuses = entries.map(entry => entry.status);
	if (statuses.every(status => status === "approved")) return "approved";
	return STATUS_PRIORITY.find(status => statuses.includes(status)) ?? "none";
};
